import { Injectable, EventEmitter } from "@angular/core";
import { HttpClient, HttpHeaders, HttpResponse } from "@angular/common/http";
import { Observable, of, throwError } from "rxjs";
import { catchError, map, take } from "rxjs/operators";
import { of as observableOf, BehaviorSubject, Subject } from "rxjs";
import { environment } from "../environments/environment";
import { StorageService, StorageKeys } from "./storage.service";
import { WindowService } from "./window.service";
import { LogService } from "./log.service";
import moment from "moment-timezone";
import { HelperService } from "./helper.service";
import { UserService } from "./user.service";

@Injectable({
  providedIn: "root",
})
export class EventsService {
  constructor(
    private http: HttpClient,
    private storage: StorageService,
    private window: WindowService,
    private _log: LogService,
    private _helpers: HelperService,
    public userService: UserService
  ) {
    this._helpers.userID = this.userService.user ? this.userService.user.id : undefined;
  }

  public eventsMap: any = {};
  private _events: any;
  get events() {
    if (!this._events) this._events = this.storage.getItem(StorageKeys.EVENTS);
    return this._events || [];
  }

  set events(events) {
    this._events = events;
    this.storage.setTimestamp("events", 24);
    this.storage.setItem(StorageKeys.EVENTS, events);
  }

  public inProgressEvents: Array<any> = [];
  public cachedPublicEvents: any[] = [];
  public cachedAllEvents: any[] = [];

  /** getEvents() Gets events. */
  public events$: Subject<any> = new Subject();
  getEvents(limit = 10, fresh?) {
    let events = this.events;

    if (events && !fresh && this.storage.checkTimestamp("events")) {
      this.processEvents(events);
      this.events$.next(this.events);
      return observableOf(events);
    } else {
      return this.http.get(environment.apiUrl + "events/list/" + (limit ? limit : ""), this._helpers.getHttpOptions()).pipe(
        map((data: any) => {
          if (data.events && data.events.length) {
            this.processEvents(data.events);
          }
          this.events$.next(this.events);
          return this.events;
        })
      );
    }
  }

  processEvents(events) {
    this.inProgressEvents = [];
    this.events = events;
    this.events.forEach((item) => {
      const regex = /data-livestream-id="([^"]+)"/gm;
      const regexEventId = /data-event-id="([^"]+)"/gm;
      let m = regex.exec(item.description_events);
      let mEventId = regexEventId.exec(item.description_events);
      if (m && m.length) item.livestream_id = m[m.length - 1];
      if (mEventId && mEventId.length) item.event_id = mEventId[mEventId.length - 1];
      if (item.startdate_events && item.starttime_events && item.enddate_events && item.endtime_events) {
        if (moment(item.start_utc) < moment() && moment(item.end_utc) > moment()) {
          this.inProgressEvents.push(item);
          item.in_progress = true;
        }
        if (moment(item.end_utc) < moment()) {
          item.ended = true;
        }
      }
      this.eventsMap[item.id_events] = item;
    });
  }

  getEvent(id) {
    if (this.events && !this.eventsMap[id]) {
      this.events.forEach((item) => (this.eventsMap[item.id_events] = item));
    }
    if (this.eventsMap[id]) {
      return observableOf(this.eventsMap[id]);
    } else {
      return this.http.get(environment.apiUrl + "events/detail/" + id, this._helpers.getHttpOptions()).pipe(
        map((data: any) => {
          return data && data.event ? data.event : false;
        })
      );
    }
  }

  clearCache() {
    this.events$.next(false);
    this._events = false;
    this.storage.removeItem(StorageKeys.EVENTS);
  }

  addressToLocation(address) {
    return this.http.get("https://maps.googleapis.com/maps/api/geocode/json?address=" + address + "&key=" + environment.googleMapsKey, this._helpers.getHttpOptions());
  }

  // --- Public methods (paeapi) ---

  getPublicEvents(limit: number = 50) {
    return this.http.get(environment.paeApiUrl + "event/list/" + limit, this._helpers.getPaeApiOptions(this._helpers.token)).pipe(
      map((data: any) => {
        const events = data.events || [];
        const allEvents = data.allEvents || [];
        this.cachedPublicEvents = events;
        this.cachedAllEvents = allEvents;
        return { events, allEvents };
      })
    );
  }

  getPublicEvent(idOrSlug: string) {
    return this.http.get(environment.paeApiUrl + "event/" + idOrSlug, this._helpers.getPaeApiOptions(this._helpers.token)).pipe(map((data: any) => data.event));
  }

  /**
   * Next-upcoming (or currently-live) livestream for an embed destination (e.g. fender.com).
   * `lang` localizes title/description server-side (falls back to English when a translation is
   * missing — see eventController.toPublicEvent).
   */
  getEmbedLivestream(destination: string, lang?: string) {
    const langParam = lang ? "&lang=" + encodeURIComponent(lang) : "";
    return this.http
      .get(environment.paeApiUrl + "event/embed-livestream?destination=" + encodeURIComponent(destination) + langParam, this._helpers.getPaeApiOptions(this._helpers.token))
      .pipe(map((data: any) => ({ event: data.event || null, events: data.events || [] })));
  }

  // --- Reminder methods (paeapi) ---

  /** Gets the current user's reminder subscription for an event series (by occurrence id or slug). */
  getEventReminder(idOrSlug: string) {
    return this.http
      .get(environment.paeApiUrl + "event/" + idOrSlug + "/reminder", this._helpers.getPaeApiOptions(this._helpers.token))
      .pipe(map((data: any) => ({ offsets: data.offsets || [], subscribed: !!data.subscribed })));
  }

  /** Sets the user's reminder for the whole series. offsets is a subset of [1440, 60] (minutes). */
  setEventReminder(idOrSlug: string, offsets: number[]) {
    return this.http
      .post(environment.paeApiUrl + "event/" + idOrSlug + "/reminder", { offsets }, this._helpers.getPaeApiOptions(this._helpers.token))
      .pipe(map((data: any) => ({ offsets: data.offsets || [], subscribed: !!data.subscribed })));
  }

  /** Cancels the user's reminder subscription for the whole series. */
  deleteEventReminder(idOrSlug: string) {
    return this.http.delete(environment.paeApiUrl + "event/" + idOrSlug + "/reminder", this._helpers.getPaeApiOptions(this._helpers.token));
  }

  /** Public URL of the .ics (Add to Calendar) file for an occurrence. */
  getEventIcsUrl(idOrSlug: string) {
    return environment.paeApiUrl + "event/" + idOrSlug + "/ics";
  }

  /**
   * Downloads the .ics file for an occurrence as a Blob (endpoint is public). paeapi returns it
   * base64-encoded, and API Gateway only decodes that when Accept is a binary media type, so the
   * default Accept would save base64 text instead of the calendar file.
   */
  downloadEventIcs(idOrSlug: string) {
    return this.http.get(this.getEventIcsUrl(idOrSlug), { headers: new HttpHeaders({ Accept: "application/octet-stream" }), responseType: "blob" });
  }

  /** Lists the current user's event reminder subscriptions (for account notification settings). */
  getMyEventReminders() {
    return this.http.get(environment.paeApiUrl + "event/reminders", this._helpers.getPaeApiOptions(this._helpers.token)).pipe(map((data: any) => data.reminders || []));
  }

  // --- Admin methods (paeapi) ---

  adminListEvents(status?: string, includePast?: boolean) {
    let url = environment.paeApiUrl + "admin/event/list";
    const params = [];
    if (status) params.push("status=" + status);
    if (includePast) params.push("past=true");
    if (params.length) url += "?" + params.join("&");
    return this.http.get(url, this._helpers.getPaeApiOptions(this._helpers.token)).pipe(map((data: any) => data.events || []));
  }

  adminCreateEvent(eventData: any) {
    return this.http.post(environment.paeApiUrl + "admin/event", eventData, this._helpers.getPaeApiOptions(this._helpers.token)).pipe(map((data: any) => data.event));
  }

  /**
   * Returns the whole response envelope, not just `event`: a series template save also reports
   * `created_occurrences` / `occurrence_cap_reached` when a recurring-schedule change fanned out
   * new dates, and the admin form surfaces that count.
   */
  adminUpdateEvent(id: string, eventData: any) {
    return this.http.put(environment.paeApiUrl + "admin/event/" + id, eventData, this._helpers.getPaeApiOptions(this._helpers.token)).pipe(map((data: any) => data));
  }

  adminDeleteEvent(id: string, deleteAllRecurring?: boolean) {
    let url = environment.paeApiUrl + "admin/event/" + id;
    if (deleteAllRecurring) url += "?delete_all_recurring=true";
    return this.http.delete(url, this._helpers.getPaeApiOptions(this._helpers.token));
  }

  adminAddOccurrence(id: string, data: any) {
    return this.http.post(environment.paeApiUrl + "admin/event/" + id + "/add-occurrence", data, this._helpers.getPaeApiOptions(this._helpers.token)).pipe(map((data: any) => data.event));
  }

  adminTranslateEvent(id: string) {
    return this.http.post(environment.paeApiUrl + "admin/event/" + id + "/translate", {}, this._helpers.getPaeApiOptions(this._helpers.token));
  }

  /**
   * Public livestream embed sign-up (DENG-930) — captures the viewer into Iterable. The server reads
   * the per-state list + source from the event's signup_config, so the client only sends which event
   * / state plus the viewer's details.
   */
  signupToLivestream(body: { event_id: string; state: string; email: string; country: string; countryCode: string; marketingOptIn: boolean }) {
    return this.http.post(environment.paeApiUrl + "event/livestream-signup", body, this._helpers.getPaeApiOptions(this._helpers.token));
  }

  adminUploadEventImage(eventId: string, filename: string, contentType: string) {
    return this.http
      .post(environment.paeApiUrl + "admin/event/upload-image", { event_id: eventId, filename, content_type: contentType }, this._helpers.getPaeApiOptions(this._helpers.token))
      .pipe(map((data: any) => data));
  }

  // --- Livestream platform helpers ---
  //
  // Two platforms are supported (Vimeo, YouTube), gated by `event.livestream_platform`. Legacy
  // events created before that field existed have an empty/undefined `livestream_platform` but
  // a populated `vimeo_livestream_id` — we coalesce those to "vimeo" at read time so old events
  // keep working without any backend migration. Consumers should never read the raw ID fields
  // directly; go through these helpers.

  /**
   * Returns the platform for a livestream event, or null if this event isn't a livestream at all.
   */
  getLivestreamPlatform(event: any): "vimeo" | "youtube" | null {
    if (!event) return null;
    if (event.livestream_platform === "vimeo") return "vimeo";
    if (event.livestream_platform === "youtube") return "youtube";
    // Legacy: no platform field. Treat as vimeo if the vimeo id is present.
    if (event.vimeo_livestream_id) return "vimeo";
    return null;
  }

  /**
   * Returns the video ID that the player should load for this livestream, or null if the event
   * isn't a livestream (or the expected ID for the resolved platform is missing).
   */
  getLivestreamVideoId(event: any): string | null {
    const platform = this.getLivestreamPlatform(event);
    if (platform === "youtube") return event.youtube_video_id || null;
    if (platform === "vimeo") return event.vimeo_video_id || null;
    return null;
  }

  /** Convenience for filter callsites. */
  isLivestreamEvent(event: any): boolean {
    return this.getLivestreamPlatform(event) !== null;
  }

  // --- Recurring schedule helpers ---
  //
  // A mirror of paeapi's `computeOccurrenceSlots` / `MAX_OCCURRENCES_PER_SAVE`
  // (paeapi/src/libraries/eventDynamo.ts), so the admin form can tell an admin what a schedule
  // will produce BEFORE they save it. The server remains the authority — this only drives copy
  // ("this will create 14 occurrences", "…more than we create per save"). Keep the two in step;
  // the specs here pin the counts per frequency so drift shows up as a failure.

  /** Ceiling on how many occurrences one save creates. Mirrors the server's per-save cap. */
  public readonly maxOccurrencesPerSave: number = 52;
  /** Hard ceiling on the walk itself, so a runaway end date can't spin. Mirrors the server. */
  private readonly maxOccurrenceSlots: number = 520;

  /**
   * Walks a recurring rule into the local wall-clock start/end slots it implies, anchored on the
   * series' own start (which fixes time of day, duration and — for monthly-by-weekday — which
   * weekday of the month it lands on).
   */
  getRecurringOccurrenceSlots(startDatetime: string, endDatetime: string, rule: any): Array<{ start: string; end: string }> {
    if (!rule || !startDatetime || rule.frequency === "intermittent" || !rule.end_date) return [];

    const parseLocal = (dt: string): Date => {
      const parts = dt.split("T");
      const [y, mo, d] = parts[0].split("-").map(Number);
      const [h, mi] = (parts[1] || "00:00").split(":").map(Number);
      return new Date(y, mo - 1, d, h, mi);
    };
    const formatLocal = (date: Date): string => {
      const pad = (n: number) => String(n).padStart(2, "0");
      return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
    };

    const startDate = parseLocal(startDatetime);
    const endDate = parseLocal(endDatetime || startDatetime);
    if (isNaN(startDate.getTime())) return [];
    const duration = Math.max(0, endDate.getTime() - startDate.getTime());
    const ruleEndDate = parseLocal(rule.end_date + (String(rule.end_date).includes("T") ? "" : "T23:59"));
    if (isNaN(ruleEndDate.getTime())) return [];

    const interval = rule.interval || 1;
    const startWeekday = startDate.getDay();
    const startNth = Math.ceil(startDate.getDate() / 7);

    const getNthWeekdayOfMonth = (year: number, month: number, weekday: number, nth: number): Date => {
      const first = new Date(year, month, 1);
      let day = 1 + ((weekday - first.getDay() + 7) % 7) + (nth - 1) * 7;
      const lastDay = new Date(year, month + 1, 0).getDate();
      if (day > lastDay) day -= 7;
      return new Date(year, month, day, startDate.getHours(), startDate.getMinutes());
    };

    const advance = (date: Date): Date => {
      const next = new Date(date);
      switch (rule.frequency) {
        case "daily":
          next.setDate(next.getDate() + interval);
          break;
        case "weekly":
          next.setDate(next.getDate() + 7 * interval);
          break;
        case "biweekly":
          next.setDate(next.getDate() + 14);
          break;
        case "monthly":
          if (rule.monthly_pattern === "weekday") {
            const nextMonth = date.getMonth() + interval;
            return getNthWeekdayOfMonth(date.getFullYear() + Math.floor(nextMonth / 12), nextMonth % 12, startWeekday, startNth);
          }
          next.setMonth(next.getMonth() + interval);
          break;
        default:
          return new Date(ruleEndDate.getTime() + 1);
      }
      return next;
    };

    const slots: Array<{ start: string; end: string }> = [];
    let current = new Date(startDate);
    let guard = 0;
    while (current <= ruleEndDate && slots.length < this.maxOccurrenceSlots && guard < this.maxOccurrenceSlots * 8) {
      guard++;
      if (rule.frequency === "weekly" && rule.days_of_week?.length && !rule.days_of_week.includes(current.getDay())) {
        current = advance(current);
        continue;
      }
      slots.push({ start: formatLocal(current), end: formatLocal(new Date(current.getTime() + duration)) });
      current = advance(current);
    }
    return slots;
  }

  /**
   * How many occurrences a schedule would newly create: future slots that no existing occurrence
   * already covers. Dates are matched by calendar day, the same way the server dedupes, so an
   * occurrence someone moved to another time that day still counts as covered.
   */
  countNewRecurringOccurrences(startDatetime: string, endDatetime: string, rule: any, existingStartDatetimes: string[] = [], timezone?: string): number {
    const covered = new Set((existingStartDatetimes || []).map((d) => (d || "").slice(0, 10)));
    const nowLocal = this.nowInTimezone(timezone);
    return this.getRecurringOccurrenceSlots(startDatetime, endDatetime, rule).filter((slot) => slot.start >= nowLocal && !covered.has(slot.start.slice(0, 10))).length;
  }

  /**
   * "Now" as a wall-clock string in `timezone`, comparable against the local datetime strings
   * events are stored as. The sv-SE locale formats as "YYYY-MM-DD HH:mm:ss".
   */
  private nowInTimezone(timezone?: string): string {
    return moment().tz(this.safeTimezone(timezone)).format("YYYY-MM-DDTHH:mm");
  }

  /** Falls back to the app-wide default for a missing or unrecognized zone. */
  private safeTimezone(timezone?: string): string {
    const zone = `${timezone || ""}`.trim();
    return zone && moment.tz.zone(zone) ? zone : "America/Chicago";
  }

  // --- Event timing helpers ---
  //
  // Events store a wall-clock start/end plus a `timezone`; some shapes (public/cached) also carry
  // precomputed `start_utc`/`end_utc`. Anything asking "has this started yet?" should go through
  // these so the timezone math lives in one place.

  /**
   * Interprets a wall-clock "YYYY-MM-DDTHH:mm" string as a time in `timezone`.
   *
   * Uses moment-timezone rather than a `toLocaleString(..., { timeZone })` round-trip, which
   * needs a full-ICU JS engine. NativeScript's engines ship without one and IGNORE those
   * options entirely, so the old implementation silently returned the wall-clock time as if it
   * were the device's local time — off by the whole offset between viewer and event, which a
   * livestream countdown shows immediately. Events on the current (DynamoDB) system carry only
   * `start_datetime` + `timezone`, so this is the path every timing call actually takes.
   */
  parseDatetimeInTimezone(dt: string, timezone: string): Date {
    return moment.tz(`${dt || ""}`, this.safeTimezone(timezone)).toDate();
  }

  /** Epoch ms for the event start, or NaN when the event has no usable start. */
  getEventStartMs(event: any): number {
    if (!event) return NaN;
    if (event.start_utc) return new Date(event.start_utc).getTime();
    if (event.start_datetime) return this.parseDatetimeInTimezone(event.start_datetime, event.timezone || "America/Chicago").getTime();
    return NaN;
  }

  /** Epoch ms for the event end. Events without an end are treated as one hour long. */
  getEventEndMs(event: any): number {
    if (!event) return NaN;
    if (event.end_utc) return new Date(event.end_utc).getTime();
    if (event.end_datetime) return this.parseDatetimeInTimezone(event.end_datetime, event.timezone || "America/Chicago").getTime();
    const start = this.getEventStartMs(event);
    return isFinite(start) ? start + 3600000 : NaN;
  }

  /**
   * Where the event sits relative to now. Returns null when the event has no parseable start —
   * callers should treat that as "don't gate on timing".
   */
  getEventTimingState(event: any, now: number = Date.now()): "upcoming" | "live" | "ended" | null {
    const start = this.getEventStartMs(event);
    const end = this.getEventEndMs(event);
    if (!isFinite(start) || !isFinite(end)) return null;
    if (now < start) return "upcoming";
    if (now <= end) return "live";
    return "ended";
  }

  /**
   * Finds the cached livestream event that plays `videoId`. Prefers one that's live right now,
   * then the soonest upcoming one — an event that recurs weekly has many occurrences sharing the
   * same video id, and the interesting one is always the current or next.
   */
  findLivestreamEventByVideoId(videoId: string): any {
    if (!videoId) return null;
    const id = String(videoId);
    const matches = (this.cachedAllEvents || []).filter((e) => String(e.vimeo_video_id) === id || String(e.vimeo_livestream_id) === id || String(e.youtube_video_id) === id);
    if (!matches.length) return null;
    const now = Date.now();
    const live = matches.find((e) => this.getEventTimingState(e, now) === "live");
    if (live) return live;
    const upcoming = matches.filter((e) => this.getEventTimingState(e, now) === "upcoming").sort((a, b) => this.getEventStartMs(a) - this.getEventStartMs(b));
    return upcoming[0] || null;
  }
}
